import { CircularProgress, Popover, Tooltip } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CSS from 'csstype';
import { cloneDeep } from 'lodash-es';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FixedSizeList } from 'react-window';

import { VirtualRow } from '~/src/dash/components/dropdowns/common';
import { ActiveQuickFilter } from '~/src/dash/components/dropdowns/SelectPill/ActiveQuickFilter';
import { EActiveFilter, statuses } from '~/src/dash/components/dropdowns/SelectPill/common';
import {
  FILTER_PILLS,
  FilterPillIconKey,
  FilterPillIcons,
  FilterPillKey,
} from '~/src/dash/components/filters/common';
import { FONT_WEIGHT } from '~/src/dash/components/text/common';
import { Col, Cols, Row, Rows, spacing, Text, TextButton, TextInput } from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';

const getHeight = (numItems: number) => {
  if (numItems >= 12) return 360;
  return numItems * 30 + 4;
};

const sortList = (list: IListItem[]) => {
  const sorted = list.sort((a, b) => {
    if ((a.selected && b.selected) || (!a.selected && !b.selected)) {
      return a.title.localeCompare(b.title);
    } else if ((!a.selected && b.selected) || (a.selected && !b.selected)) {
      return a.selected > b.selected ? -1 : 1;
    } else {
      return 0;
    }
  });
  return sorted;
};

const queryList = (query: string, list: IListItem[]) => {
  let queriedList = cloneDeep(list);
  if (query) {
    const casedQuery = query.toLowerCase();
    queriedList = list.filter(
      (item) => item.ID.includes(query) || item.title.toLowerCase().includes(casedQuery),
    );
  }
  return queriedList;
};

const extractActiveFilteredList = (list: IListItem[], activeStatus: EActiveFilter) => {
  switch (activeStatus) {
    case EActiveFilter.SHOW_ALL:
      return list;
    case EActiveFilter.ACTIVE:
      return list.filter((item) => item.active);
    case EActiveFilter.INACTIVE:
      return list.filter((item) => !item.active);
  }
};

const getSelectedCountFromSorted = (list: IListItem[]) => {
  let count = 0;
  for (const item of list) {
    if (item.selected) {
      count++;
    } else {
      break;
    }
  }
  return count;
};

const presetStyle = {
  default: {
    color: publicaColors.veryDarkGray,
    backgroundColor: publicaColors.veryLightGray,
  },
  active: {
    color: publicaColors.jungleGreen,
    backgroundColor: publicaColors.honeydew,
  },
  disabled: {
    color: publicaColors.gray,
    backgroundColor: publicaColors.lightGray,
    cursor: 'not-allowed',
  },
  open: {
    backgroundColor: publicaColors.lightGray,
  },
  refresh: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
} as Record<string, CSS.Properties>;

const getPillColor = (active: boolean, disabled: boolean) => {
  if (active) return presetStyle.active.color;
  if (disabled) return presetStyle.disabled.color;
  return presetStyle.default.color;
};

interface ITogglePillProps {
  className?: string;
  customLabel?: string | FunctionComponent;
  tooltip?: string;
  displayArrow?: boolean;
  icon?: FilterPillIconKey;
  isActive: boolean;
  isDisabled?: boolean;
  isOpen?: boolean;
  isRefreshing?: boolean; // disables pill and displays a loading circle animation
  label?: string;
  onClick: (props?: any | null) => void;
  style?: CSS.Properties;
}

const TogglePill = ({
  className = '',
  customLabel,
  tooltip = '',
  displayArrow,
  icon,
  isActive = false,
  isDisabled = false,
  isOpen = false,
  isRefreshing = false,
  label = '',
  onClick,
  style,
}: ITogglePillProps) => {
  const PillIcon = icon ? FilterPillIcons[icon] : undefined;

  return (
    <Tooltip title={tooltip} placement='top'>
      <button
        onClick={isRefreshing || isDisabled ? () => null : onClick}
        className={className}
        style={{
          borderRadius: '5px',
          borderWidth: 0,
          cursor: 'pointer',
          display: 'inline-block',
          fontFamily: 'inherit',
          fontStyle: 'inherit',
          fontWeight: FONT_WEIGHT.bold,
          lineHeight: 'inherit',
          margin: 0,
          minWidth: '40px',
          padding: '4px 12px',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          ...presetStyle.default,
          ...(isDisabled ? presetStyle.disabled : {}),
          ...(isOpen ? presetStyle.open : {}),
          ...(isActive ? presetStyle.active : {}),
          ...(isRefreshing ? presetStyle.refresh : {}),
          ...style,
        }}
      >
        <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
          {PillIcon && (
            <Col flex style={PillIcon.style}>
              <PillIcon.SvgComponent color={getPillColor(isActive, isDisabled)} />
            </Col>
          )}
          <Col>{customLabel || label}</Col>
          {isRefreshing && (
            <Col displayFlex>
              <CircularProgress size={10} />
            </Col>
          )}
          {displayArrow && (
            <Col
              displayFlex // this is to center the image vertically
            >
              <ArrowDropDownIcon style={{ color: publicaColors.darkGray }} />
            </Col>
          )}
        </Cols>
      </button>
    </Tooltip>
  );
};

interface ISelectPillProps {
  currentSelected: Record<string, IListItem>; // current object of selected options
  tooltip?: string;
  displayID?: boolean; // determines whether to display the option ID
  displayArrow?: boolean;
  handleAccept: (
    key: string,
    allOptions: Record<string, IListItem>,
    selectedOptions: Record<string, IListItem>,
  ) => void;
  handleOnOpen?: () => void; // function to fire on open
  // function to fire on close.
  // IMPORTANT! remember handle accept will fire handleOnClose so try to avoid duplicate code.
  // the primary reason to use this is to fire a function when hasChangeOccurred is false that is not used on handleAccept.
  handleOnClose?: (hasChangeOccurred: boolean) => void;
  icon?: FilterPillIconKey;
  isActive: boolean;
  isDisabled?: boolean;
  isRefreshing?: boolean;
  isSubset?: boolean; // scenario when selecting all is not the same as selecting none. all selected options will persist and send on accept
  filterKey: string;
  options: Record<string, IListItem>;
  selectedLimit?: number; // sets the maximum amount of options that can be selected. if no selected limit, the select all option will appear. please see isSubset above as it may apply
  customLabel?: string | FunctionComponent; // jsx that will override default filter menuTitle
  isSelectionRequired?: boolean; // cannot deselect a singular option or clear the filter
  disabledOptionTooltip?: string; // tooltip message to display if selection is disabled
  enableActiveFilter?: boolean; // adds active/inactive/show all filter. must contain the active prop in IListItem
}

export const SelectPill = ({
  currentSelected = {},
  customLabel,
  displayID = false, // determines whether to display the option ID
  displayArrow = false,
  tooltip = '',
  handleAccept,
  handleOnClose,
  handleOnOpen,
  icon,
  isActive,
  isDisabled,
  isRefreshing,
  isSelectionRequired,
  isSubset = false,
  filterKey,
  options,
  selectedLimit,
  disabledOptionTooltip,
  enableActiveFilter,
}: ISelectPillProps) => {
  const isMounted = useRef(false);
  const hasChangeOccurred = useRef(false);
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [clonedOptions, setClonedOptions] = useState({} as Record<string, IListItem>);
  const [sortedList, setSortedList] = useState<IListItem[]>([]);
  const [filteredList, setFilteredList] = useState<IListItem[]>([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [query, setQuery] = useState('');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState(0);

  const isSingleSelect = selectedLimit === 1;
  const totalCount = Object.values(options || []).length;
  const label = filterKey ? FILTER_PILLS[filterKey as FilterPillKey].menuTitle : '';

  // we clone the incoming options to preserve the original state until apply
  useEffect(() => {
    if (options) {
      setClonedOptions(options || []);
    }
  }, [options, currentSelected]);

  useEffect(() => {
    const listArray = Object.values(clonedOptions);
    const sorted = sortList(listArray);
    unstable_batchedUpdates(() => {
      setSortedList(sorted);
      setSelectedCount(getSelectedCountFromSorted(sorted));
      setQuery('');
    });
  }, [clonedOptions]);

  // filter options upon search
  useEffect(() => {
    if (isMounted.current) {
      const queriedList = queryList(query, sortedList);
      const activeFilteredList = extractActiveFilteredList(queriedList, selectedActiveStatus);
      setFilteredList(activeFilteredList);
    }
    isMounted.current = true;
  }, [query, sortedList, selectedActiveStatus]);

  const handleOpen = (e: React.MouseEvent) => {
    setAnchorEl(e.currentTarget);
    handleOnOpen && handleOnOpen();
  };

  const handleClose = () => {
    unstable_batchedUpdates(() => {
      setAnchorEl(null);
      setQuery('');
    });
    handleOnClose && handleOnClose(hasChangeOccurred.current);
  };

  const handleChange = (id: string) => {
    if (isSingleSelect) {
      if (clonedOptions[id].selected) {
        if (isSelectionRequired && selectedCount === 1) return;
        clonedOptions[id].selected = false;
      } else if (selectedCount === 1) {
        const previouslySelectedID = sortedList[0].ID;
        clonedOptions[previouslySelectedID].selected = false;
        clonedOptions[id].selected = true;
      } else {
        clonedOptions[id].selected = true;
      }
      const sorted = sortList(Object.values(clonedOptions));
      hasChangeOccurred.current = true;
      unstable_batchedUpdates(() => {
        setSortedList(sorted);
        setSelectedCount(getSelectedCountFromSorted(sorted));
      });
    } else if (
      !selectedLimit ||
      clonedOptions[id].selected ||
      (!clonedOptions[id].selected && selectedLimit && selectedCount < selectedLimit)
    ) {
      clonedOptions[id].selected = !clonedOptions[id].selected;
      const sorted = sortList(Object.values(clonedOptions));
      hasChangeOccurred.current = true;
      unstable_batchedUpdates(() => {
        setSortedList(sorted);
        setSelectedCount(getSelectedCountFromSorted(sorted));
      });
    }
  };

  const handleSelectAll = () => {
    Object.keys(clonedOptions).forEach((key) => {
      clonedOptions[key].selected = true;
    });
    const sorted = sortList(Object.values(clonedOptions));
    hasChangeOccurred.current = true;
    unstable_batchedUpdates(() => {
      setSelectedCount(totalCount);
      setSortedList(sorted);
    });
  };

  const handleClear = () => {
    Object.keys(clonedOptions).forEach((key) => {
      clonedOptions[key].selected = false;
    });
    const sorted = sortList(Object.values(clonedOptions));
    hasChangeOccurred.current = true;
    unstable_batchedUpdates(() => {
      setSelectedCount(0);
      setSortedList(sorted);
    });
  };

  const handleApply = () => {
    const selected = {} as Record<string, IListItem>;
    if (hasChangeOccurred.current || (!isActive && selectedCount && !hasChangeOccurred.current)) {
      if (selectedCount < totalCount || isSubset) {
        for (const item of sortedList) {
          if (item.selected) {
            selected[item.ID] = item;
          } else {
            break;
          }
        }
      } else {
        // if all options are selected and not a subset, set them all to false
        // selecting all in this scenario is the same as selecting none
        for (const item of sortedList) {
          clonedOptions[item.ID].selected = false;
        }
      }
      handleAccept(filterKey, clonedOptions, selected);
    }
    hasChangeOccurred.current = false;
    handleClose();
  };

  const handleChangeActiveFilter = (status: number) => {
    setSelectedActiveStatus(status);
  };

  const open = Boolean(anchorEl);
  const listCount = filteredList.length;

  return (
    <div>
      <TogglePill
        isActive={isActive}
        isDisabled={isDisabled}
        tooltip={tooltip}
        isOpen={!!anchorEl}
        isRefreshing={isRefreshing}
        icon={icon}
        onClick={handleOpen}
        label={label}
        customLabel={customLabel}
        displayArrow={displayArrow}
      />
      <Popover
        id={`List-${label.trim()}`}
        open={open}
        anchorEl={anchorEl}
        onClose={handleApply}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            overflow: 'hidden',
            width: '480px',
            border: `1px solid ${publicaColors.lightGray}`,
            boxShadow: 'none',
          },
        }}
      >
        <Rows>
          {/* STICKY SECTION */}
          <Row
            noPadding
            style={{
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Rows>
              <Row smallPadding>
                <TextInput
                  enableClear
                  fullWidth
                  handleOnChange={setQuery}
                  inputSize='small'
                  isAutoFocused
                  msDelay={500}
                  placeholder={`Search`}
                  prefix='search'
                />
              </Row>
              <Row style={{ padding: '0px 8px 6px' }}>
                <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Col flex>
                    <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                      {!selectedLimit && (
                        <Col>
                          <TextButton
                            size='small'
                            onClick={selectedCount < totalCount ? handleSelectAll : handleClear}
                          >
                            Select All
                          </TextButton>
                        </Col>
                      )}
                      <Col>
                        <Text muted small disableSelect>
                          {selectedLimit
                            ? `${selectedCount} /${selectedLimit} Selected`
                            : `${selectedCount} Selected`}
                        </Text>
                      </Col>
                      {enableActiveFilter && selectedActiveStatus > 0 && (
                        <Col>
                          <Text highlighted extraSmall disableSelect>
                            {statuses.find((status) => status.value === selectedActiveStatus)
                              ?.message || ''}
                          </Text>
                        </Col>
                      )}
                    </Cols>
                  </Col>
                  <Cols
                    gap={spacing.base}
                    style={{ justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {!isSelectionRequired && selectedCount > 0 && (
                      <Col style={{ paddingTop: '1px' }}>
                        <TextButton size='small' onClick={handleClear}>
                          Clear
                        </TextButton>
                      </Col>
                    )}
                    {enableActiveFilter && (
                      <Col displayFlex>
                        <ActiveQuickFilter
                          selectedStatus={selectedActiveStatus}
                          handleChange={handleChangeActiveFilter}
                        />
                      </Col>
                    )}
                  </Cols>
                </Cols>
              </Row>
            </Rows>
          </Row>
          {/* LIST SECTION */}
          <Row noPadding>
            <FixedSizeList
              height={getHeight(listCount)}
              itemCount={listCount}
              itemSize={30}
              width={480}
            >
              {({ index, style }) =>
                VirtualRow({
                  index,
                  style,
                  // @ts-ignore
                  displayID,
                  handleChange,
                  options: filteredList,
                  disabledOptionTooltip,
                })
              }
            </FixedSizeList>
          </Row>
        </Rows>
      </Popover>
    </div>
  );
};
